import { Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable } from 'rxjs';
import { environment } from 'src/environments/environment';

export interface UpdatedField {
  field: string;
  from: any;
  to: any;
}

export interface AssetLogRecord {
  _id: string;
  assetType: 'Inventory' | 'Workstation';
  assetId: string;
  assetName: string;
  assetCode?: string;
  action:
    | 'Create'
    | 'Bulk Create'
    | 'Update'
    | 'Delete'
    | 'Assign'
    | 'Unassign'
    | 'Return'
    | 'Extend'
    | 'Images Added'
    | 'Images Removed'
    | 'Images Submitted'
    | 'Request Raised'
    | 'Request Forwarded'
    | 'Request Approved'
    | 'Request Rejected'
    | 'Request On Hold'
    | string;
  actorId: string;
  actorName: string;
  actorProfileImg?: string;
  targetUserId?: string;
  targetUserName?: string;
  meta: Record<string, any>;
  createdAt: string;
  updatedAt?: string;
}

export interface AssetLogsParams {
  page?: number;
  limit?: number;
  assetType?: 'Inventory' | 'Workstation';
  assetId?: string;
  startDate?: string;
  endDate?: string;
}

export interface AssetLogsResponse {
  data: {
    totalCounts: number;
    records: AssetLogRecord[];
  };
  message: string;
}

/**
 * Checks if the logged-in user is authorized to view asset logs.
 * Only users whose department is ADMIN or HR-HEAD are permitted.
 */
export function canViewAssetLogs(): boolean {
  const dept = (localStorage.getItem('department') || '').trim().toUpperCase();
  return dept === 'ADMIN' || dept === 'HR-HEAD';
}

/**
 * Formats a date string/Date to ISO string starting at 00:00:00.000 with local timezone offset.
 */
export function formatStartDateToISO(date: Date | string): string {
  if (!date) return '';
  const d = typeof date === 'string' ? new Date(date) : new Date(date.getTime());
  d.setHours(0, 0, 0, 0);
  return formatWithTimezoneOffset(d);
}

/**
 * Formats a date string/Date to ISO string ending at 23:59:59.999 with local timezone offset.
 * Example: 2026-10-21T23:59:59.999+05:30
 */
export function formatEndDateToISO(date: Date | string): string {
  if (!date) return '';
  const d = typeof date === 'string' ? new Date(date) : new Date(date.getTime());
  d.setHours(23, 59, 59, 999);
  return formatWithTimezoneOffset(d);
}

function formatWithTimezoneOffset(d: Date): string {
  const pad = (n: number, z = 2) => ('00' + n).slice(-z);
  const year = d.getFullYear();
  const month = pad(d.getMonth() + 1);
  const day = pad(d.getDate());
  const hours = pad(d.getHours());
  const minutes = pad(d.getMinutes());
  const seconds = pad(d.getSeconds());
  const ms = pad(d.getMilliseconds(), 3);

  const offset = -d.getTimezoneOffset();
  const sign = offset >= 0 ? '+' : '-';
  const offsetHours = pad(Math.floor(Math.abs(offset) / 60));
  const offsetMinutes = pad(Math.abs(offset) % 60);

  return `${year}-${month}-${day}T${hours}:${minutes}:${seconds}.${ms}${sign}${offsetHours}:${offsetMinutes}`;
}

@Injectable({
  providedIn: 'root'
})
export class AssetLogsService {
  private readonly baseUrl = `${environment.host}asset-logs`;

  constructor(private http: HttpClient) {}

  /**
   * Check department authorization
   */
  canViewLogs(): boolean {
    return canViewAssetLogs();
  }

  /**
   * Fetch asset logs with optional filtering and pagination
   */
  getAssetLogs(params: AssetLogsParams): Observable<AssetLogsResponse> {
    let httpParams = new HttpParams();
    if (params.page !== undefined && params.page !== null) {
      httpParams = httpParams.set('page', params.page.toString());
    }
    if (params.limit !== undefined && params.limit !== null) {
      httpParams = httpParams.set('limit', params.limit.toString());
    }
    if (params.assetType) {
      httpParams = httpParams.set('assetType', params.assetType);
    }
    if (params.assetId) {
      httpParams = httpParams.set('assetId', params.assetId);
    }
    if (params.startDate) {
      httpParams = httpParams.set('startDate', params.startDate);
    }
    if (params.endDate) {
      httpParams = httpParams.set('endDate', params.endDate);
    }

    return this.http.get<AssetLogsResponse>(this.baseUrl, { params: httpParams });
  }

  /**
   * Helper to build a readable one-line summary from log action and meta
   */
  buildMetaSummary(record: AssetLogRecord): string {
    const meta = record?.meta || {};
    const action = record?.action || '';

    switch (action) {
      case 'Create':
        return `${record.assetType} was created`;

      case 'Bulk Create': {
        const added = meta.totalAdded ?? 0;
        const existing = meta.totalExisting ?? 0;
        return `Bulk created: ${added} added, ${existing} existing`;
      }

      case 'Update': {
        const fields = meta.updatedFields as UpdatedField[] | undefined;
        if (fields && Array.isArray(fields) && fields.length > 0) {
          const names = fields.map(f => f.field).join(', ');
          return `Updated ${fields.length} field${fields.length > 1 ? 's' : ''}: ${names}`;
        }
        return 'Fields updated';
      }

      case 'Delete':
        return `${record.assetType} was deleted`;

      case 'Assign': {
        const target = record.targetUserName ? ` to ${record.targetUserName}` : '';
        const project = meta.projectName ? ` (Project: ${meta.projectName})` : '';
        return `Assigned${target}${project}`;
      }

      case 'Unassign': {
        const target = record.targetUserName ? ` from ${record.targetUserName}` : '';
        return `Unassigned${target}`;
      }

      case 'Return': {
        const target = record.targetUserName ? ` by ${record.targetUserName}` : '';
        return `Inventory returned${target}`;
      }

      case 'Extend': {
        const fromDate = this.formatShortDate(meta.from);
        const toDate = this.formatShortDate(meta.to);
        if (fromDate && toDate) {
          return `Return extended: ${fromDate} → ${toDate}`;
        }
        return 'Return date extended';
      }

      case 'Images Added': {
        const count = Array.isArray(meta.images) ? meta.images.length : 0;
        return `${count} image${count !== 1 ? 's' : ''} added`;
      }

      case 'Images Removed': {
        const count = Array.isArray(meta.images) ? meta.images.length : 0;
        return `${count} image${count !== 1 ? 's' : ''} removed`;
      }

      case 'Images Submitted': {
        const reqType = meta.requestType ? `${meta.requestType}: ` : '';
        const count = Array.isArray(meta.images) ? meta.images.length : 0;
        return `${reqType}${count} image${count !== 1 ? 's' : ''} submitted`;
      }

      case 'Request Raised': {
        const reqType = meta.requestType || 'Request raised';
        const project = meta.projectName ? ` · ${meta.projectName}` : '';
        const urgency = meta.urgency ? ` (${meta.urgency})` : '';
        return `${reqType}${project}${urgency}`;
      }

      case 'Request Forwarded': {
        const admin = meta.adminName || meta.adminId ? ` to ${meta.adminName || meta.adminId}` : '';
        const cost = meta.maximumCost ? ` · Max: ₹${meta.maximumCost}` : '';
        return `Forwarded${admin}${cost}`;
      }

      case 'Request Approved': {
        const reqType = meta.requestType || 'Request';
        const by = meta.decidedBy ? ` by ${meta.decidedBy}` : '';
        return `${reqType} approved${by}`;
      }

      case 'Request Rejected': {
        const reqType = meta.requestType || 'Request';
        const reason = meta.rejectionReason ? `: ${meta.rejectionReason}` : '';
        return `${reqType} rejected${reason}`;
      }

      case 'Request On Hold': {
        const reqType = meta.requestType || 'Request';
        const till = meta.onHoldTill ? ` until ${this.formatShortDate(meta.onHoldTill)}` : '';
        return `${reqType} on hold${till}`;
      }

      default:
        return action;
    }
  }

  private formatShortDate(val: any): string {
    if (!val) return '';
    try {
      const d = new Date(val);
      if (isNaN(d.getTime())) return String(val);
      return d.toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' });
    } catch {
      return String(val);
    }
  }

  /**
   * Returns generic key-value pairs excluding keys already handled in specialized UI blocks
   */
  getGenericMetaEntries(meta: Record<string, any>, action?: string): { key: string; value: any }[] {
    if (!meta || typeof meta !== 'object') return [];

    // Keys that are rendered in dedicated UI widgets
    const specialKeys = new Set([
      'updatedFields',
      'images',
      'from',
      'to',
      'rejectionReason',
      'onHoldTill',
      'requestType',
      'requestId',
      'decidedBy',
      'adminId',
      'adminName',
      'maximumCost',
      'comment',
      'totalAdded',
      'totalExisting',
      'addedProductIds'
    ]);

    return Object.entries(meta)
      .filter(([key, val]) => !specialKeys.has(key) && val !== undefined && val !== null && val !== '')
      .map(([key, value]) => ({ key, value }));
  }
}
